import { Component, Input, Output, EventEmitter, OnInit } from '@angular/core';
import { WeightStationMessage } from '../../../models/mqtt/weight-station-message';
import { GlobalMessageUtil } from '@card-software/nexus-utilities';

export interface WeightData {
  weight: number;
  isManual: boolean;
  timestamp: Date;
}

@Component({
  selector: 'app-weight-control',
  templateUrl: './weight-control.component.html',
  styleUrl: './weight-control.component.scss',
})
export class WeightControlComponent implements OnInit {
  @Input() title = 'Weight Control';
  @Input() tagText?: string;
  @Input() tagClass = 'bg-blue-400 text-blue-800';
  @Input() scaleData: WeightStationMessage | null = null;
  @Input() initialWeight = 0;
  @Input() minWeight = 0;
  @Input() disabled = false;
  @Input() savedWeight?: WeightData;
  @Input() rollNumber?: string; // Roll number to print

  @Output() weightSaved = new EventEmitter<WeightData>();
  @Output() weightReset = new EventEmitter<void>();
  @Output() printRequested = new EventEmitter<string>();
  @Output() printWeight = new EventEmitter<void>();

  manualWeightOverride = false;
  manualWeightValue = 0;

  ngOnInit(): void {
    // If we have saved weight, populate the manual values
    if (this.savedWeight) {
      this.manualWeightOverride = this.savedWeight.isManual;
      this.manualWeightValue = this.savedWeight.weight;
    }
  }

  onSaveWeight(): void {
    const weight = this.manualWeightOverride ? this.manualWeightValue : this.getCurrentWeight();

    // Validate that weight is greater than 0
    if (weight <= 0) {
      GlobalMessageUtil.error('Invalid Weight', 'Weight must be greater than 0 lbs', 3000);
      return;
    }

    // Validate that weight does not exceed the initial/original weight
    if (this.initialWeight > 0 && weight > this.initialWeight) {
      GlobalMessageUtil.error(
        'Weight Exceeds Limit',
        `Weight cannot exceed the original weight of ${this.initialWeight} lbs. Current value: ${weight} lbs`,
        5000,
      );
      return;
    }

    // Validate that weight meets the minimum required weight
    if (this.minWeight > 0 && weight < this.minWeight) {
      GlobalMessageUtil.error(
        'Weight Below Minimum',
        `Weight cannot be less than the required weight of ${this.minWeight} lbs. Current value: ${weight} lbs`,
        5000,
      );
      return;
    }

    const weightData: WeightData = {
      weight,
      isManual: this.manualWeightOverride,
      timestamp: new Date(),
    };

    this.weightSaved.emit(weightData);
  }

  onResetWeight(): void {
    this.manualWeightOverride = false;
    this.manualWeightValue = 0;
    this.weightReset.emit();
  }

  onPrintWeight(): void {
    if (this.rollNumber && this.canPrint()) {
      this.printRequested.emit(this.rollNumber);
    }
  }

  canPrint(): boolean {
    return this.isWeightSaved() && this.getDisplayWeight() > 0 && this.disabled;
  }

  getCurrentWeight(): number {
    if (this.savedWeight) {
      return this.savedWeight.weight;
    }

    if (this.scaleData?.weight) {
      return this.scaleData.weight;
    }

    return 0;
  }

  getDisplayWeight(): number {
    if (this.savedWeight) {
      return this.savedWeight.weight;
    }

    if (this.manualWeightOverride) {
      return this.manualWeightValue;
    }

    return this.getCurrentWeight();
  }

  isWeightSaved(): boolean {
    return !!this.savedWeight;
  }

  isWeightValid(): boolean {
    const weight = this.manualWeightOverride ? this.manualWeightValue : this.getCurrentWeight();

    // Weight must be greater than 0
    if (weight <= 0) {
      return false;
    }

    // Weight cannot exceed initial weight (if set)
    if (this.initialWeight > 0 && weight > this.initialWeight) {
      return false;
    }

    // Weight cannot be below minimum weight (if set)
    if (this.minWeight > 0 && weight < this.minWeight) {
      return false;
    }

    return true;
  }
}
